import {
  InsightConflict,
  InsightDecision,
  KeyMoment,
  MeetingInsights,
  PresentationDeck,
  SlideDeckSummary,
  SlideSummaryItem,
  SlideTimelineEntry,
  TaggedReplica,
} from '../../../services/api.service';
import { VideoMoment, buildVideoMoments } from './video-moments.utils';

export type SlideBadgeType = 'kpi' | 'conflict' | 'decision' | 'risk' | 'task';

export const BADGE_COLORS: Record<SlideBadgeType, string> = {
  conflict: '#EF4444',
  kpi: '#3B82F6',
  decision: '#10B981',
  risk: '#F59E0B',
  task: '#F59E0B',
};

export const BADGE_LABELS: Record<SlideBadgeType, string> = {
  conflict: 'спор',
  kpi: 'KPI',
  decision: 'решение',
  risk: 'риск',
  task: 'задача',
};

export type SlidePanelItem = {
  ts: number;
  slide_index: number;
  title: string;
  caption: string;
  badgeType: SlideBadgeType | null;
  timestamp?: string;
};

export type SlideSection = {
  id: number;
  title: string;
  slides: SlidePanelItem[];
  start_ts: number;
  end_ts: number;
};

export type SectionSummaryCard = {
  num: number;
  title: string;
  summary: string;
  start_ts: number;
  badges: SlideBadgeType[];
};

const PRESENTATION_TYPES = new Set(['slide', 'screen_share', 'spreadsheet', 'diagram']);

function oneLine(text: string, max = 96): string {
  const t = text.trim().replace(/\s+/g, ' ');
  if (!t) return '';
  const first = t.split(/[.!?]\s/)[0];
  const line = first.length < t.length ? `${first}.` : t;
  return line.length > max ? `${line.slice(0, max - 1)}…` : line;
}

function slideCaption(
  slide: SlideTimelineEntry,
  ai?: SlideSummaryItem,
): string {
  if (ai?.summary) return oneLine(ai.summary);
  if (slide.summary) return oneLine(slide.summary);
  if (slide.bullets?.length) return oneLine(slide.bullets.join(' · '));
  if (slide.ocr_text) return oneLine(slide.ocr_text);
  if (slide.title) return oneLine(slide.title);
  return 'Содержимое слайда';
}

function slideTitle(slide: SlideTimelineEntry, ai?: SlideSummaryItem): string {
  return ai?.title?.trim() || slide.title?.trim() || `Слайд ${(slide.slide_index ?? 0) + 1}`;
}

function momentToBadge(m: VideoMoment): SlideBadgeType | null {
  if (m.type === 'dispute') return 'conflict';
  if (m.type === 'KPI') return 'kpi';
  if (m.type === 'decision') return 'decision';
  if (m.type === 'risk') return 'risk';
  if (m.type === 'task') return 'task';
  return null;
}

function badgeNearTime(moments: VideoMoment[], ts: number, windowSec = 35): SlideBadgeType | null {
  let best: VideoMoment | undefined;
  let bestDelta = Infinity;
  moments.forEach(m => {
    const delta = Math.abs(m.seconds - ts);
    if (delta <= windowSec && delta < bestDelta) {
      best = m;
      bestDelta = delta;
    }
  });
  return best ? momentToBadge(best) : null;
}

function badgesInRange(moments: VideoMoment[], start: number, end: number): SlideBadgeType[] {
  const set = new Set<SlideBadgeType>();
  moments.forEach(m => {
    if (m.seconds >= start - 5 && m.seconds <= end + 5) {
      const b = momentToBadge(m);
      if (b) set.add(b);
    }
  });
  return Array.from(set);
}

export function buildSlideSections(
  presentationDecks: PresentationDeck[],
  presentationSlides: SlideTimelineEntry[],
  slideTimeline: SlideTimelineEntry[],
  slideDeckSummary: SlideDeckSummary | null | undefined,
  moments: VideoMoment[],
): SlideSection[] {
  const aiByIndex = new Map<number, SlideSummaryItem>();
  slideDeckSummary?.slide_summaries?.forEach(s => {
    if (s.slide_index != null) aiByIndex.set(s.slide_index, s);
  });

  const deckSlides = presentationSlides.length
    ? presentationSlides
    : slideTimeline.filter(
        s => s.is_presentation !== false && PRESENTATION_TYPES.has(s.content_type || ''),
      );

  const decks: PresentationDeck[] = presentationDecks.length
    ? presentationDecks
    : deckSlides.length
      ? [{
          deck_id: 1,
          title: deckSlides[0]?.title || 'Презентация',
          slides: deckSlides,
          start_sec: deckSlides[0]?.time_sec,
          end_sec: deckSlides[deckSlides.length - 1]?.time_sec,
        }]
      : [];

  return decks.map((deck, idx) => {
    const slides = (deck.slides || []).map((slide, i) => {
      const ai = slide.slide_index != null ? aiByIndex.get(slide.slide_index) : undefined;
      const ts = slide.time_sec ?? 0;
      return {
        ts,
        slide_index: slide.slide_index ?? i,
        title: slideTitle(slide, ai),
        caption: slideCaption(slide, ai),
        badgeType: badgeNearTime(moments, ts),
        timestamp: slide.timestamp,
      };
    });

    const start_ts = deck.start_sec ?? slides[0]?.ts ?? 0;
    const end_ts = deck.end_sec ?? slides[slides.length - 1]?.ts ?? start_ts;

    return {
      id: deck.deck_id ?? idx + 1,
      title: deck.title || `Раздел ${idx + 1}`,
      slides,
      start_ts,
      end_ts,
    };
  });
}

export function buildSectionSummaryCards(
  sections: SlideSection[],
  slideDeckSummary: SlideDeckSummary | null | undefined,
  moments: VideoMoment[],
): SectionSummaryCard[] {
  const aiByIndex = new Map<number, SlideSummaryItem>();
  slideDeckSummary?.slide_summaries?.forEach(s => {
    if (s.slide_index != null) aiByIndex.set(s.slide_index, s);
  });

  return sections
    .filter(s => s.slides.length > 0)
    .map((section, idx) => {
      const summaries: string[] = [];
      section.slides.forEach(sl => {
        const ai = aiByIndex.get(sl.slide_index);
        if (ai?.summary?.trim()) summaries.push(ai.summary.trim());
        else if (sl.caption) summaries.push(sl.caption);
      });

      let summary = '';
      if (summaries.length >= 2) {
        summary = summaries.slice(0, 3).join(' ');
      } else if (summaries.length === 1) {
        summary = summaries[0];
      } else {
        summary = section.slides.map(s => s.caption).filter(Boolean).slice(0, 2).join(' ');
      }

      if (summary.length > 420) {
        summary = `${summary.slice(0, 417)}…`;
      }

      return {
        num: idx + 1,
        title: section.title,
        summary: summary || 'Содержание раздела по данным VLM.',
        start_ts: section.start_ts,
        badges: badgesInRange(moments, section.start_ts, section.end_ts),
      };
    });
}

export function findActiveSlideIndex(
  sections: SlideSection[],
  currentTime: number,
): { sectionId: number; slideIndex: number } | null {
  let best: { sectionId: number; slideIndex: number; ts: number } | undefined;
  sections.forEach(section => {
    section.slides.forEach(sl => {
      if (sl.ts <= currentTime + 0.5) {
        if (!best || sl.ts >= best.ts) {
          best = { sectionId: section.id, slideIndex: sl.slide_index, ts: sl.ts };
        }
      }
    });
  });
  return best ? { sectionId: best.sectionId, slideIndex: best.slideIndex } : null;
}

export function countMomentsByBadge(moments: VideoMoment[]): Record<SlideBadgeType, number> {
  const counts: Record<SlideBadgeType, number> = {
    decision: 0,
    kpi: 0,
    conflict: 0,
    risk: 0,
    task: 0,
  };
  moments.forEach(m => {
    const b = momentToBadge(m);
    if (b) counts[b] += 1;
  });
  return counts;
}

export { buildVideoMoments };
